import {
  faCalendarDays,
  faChevronLeft,
  faChevronRight,
  faEnvelope,
  faReply,
  faPaperPlane,
  faTimes,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import classNames from "classnames";
import { dateFormatter } from "helpers/utils";
import { useAtom } from "jotai";
import { paginationAtom } from "jotai/LeadsJotaiInstance";
import { Pagination, Card, Badge, Form, Button, Collapse } from "react-bootstrap";
import { useState } from "react";
import ActivityTimelineSkeleton from "components/common/skeletons/ActivityTimelineSkeleton";
import { 
  UilPhone, 
  UilCalendarAlt, 
  UilEnvelope,
  UilCheckCircle,
  UilExclamationTriangle,
  UilUser,
  UilClipboard
} from "@iconscout/react-unicons";
import EmailDetailModal from './EmailDetailModal';
import { decodeEmailBody } from "helpers/email";
import axiosInstance from "helpers/api";

interface LeadDetailsTimelineProps {
  activities: any[];
  allActivities: any[];
  isLoading?: boolean;
  handleSearchInputChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  leadId?: string;
}

const LeadDetailsTimeline = ({ activities, allActivities, isLoading = false, handleSearchInputChange, leadId }: LeadDetailsTimelineProps) => {
  const [pagination, setPagination] = useAtom(paginationAtom);
  const { limit, offset, total } = pagination;
  const [showModal, setShowModal] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<any>(null);
  const [expandedEmailId, setExpandedEmailId] = useState<number | null>(null);
  const [replyText, setReplyText] = useState<string>('');
  const [isLoadingEmailDetails, setIsLoadingEmailDetails] = useState(false);

  const currentPage = Math.floor(offset / limit) + 1;
  const totalPages = Math.ceil(total / limit);
  const maxVisiblePages = 4;

  const handlePageChange = (pageNumber: number) => {
    setPagination((prev) => ({
      ...prev,
      offset: pageNumber - 1,
      // offset: (pageNumber - 1) * limit,
    }));
  };

  const handleNext = () => {
    if (currentPage < totalPages) {
      handlePageChange(currentPage + 1);
    }
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      handlePageChange(currentPage - 1);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const startPage = Math.max(1, currentPage - 1);
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <Pagination.Item
          key={i}
          active={i === offset + 1}
          onClick={() => handlePageChange(i)}
        >
          {i}
        </Pagination.Item>
      );
    }
    return pages;
  };

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'call_note': return UilPhone;
      case 'email': return UilEnvelope;
      case 'meeting': return UilCalendarAlt;
      case 'task': return UilCheckCircle;
      case 'note': return UilClipboard;
      default: return UilUser;
    }
  };

  const getActivityType = (type: string) => {
    switch (type) {
      case 'call_note': return { type: 'Call', color: 'warning' };
      case 'email': return { type: 'Email', color: 'danger' };
      case 'meeting': return { type: 'Meeting', color: 'info' };
      case 'task': return { type: 'Task', color: 'success' };
      case 'note': return { type: 'Note', color: 'primary' };
      default: return { type: 'Activity', color: 'secondary' };
    }
  };

  const getTimeAgo = (dateStr: string) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60));
    
    if (diffInHours < 1) return 'Just now';
    if (diffInHours < 24) return `${diffInHours}h ago`;
    const diffInDays = Math.floor(diffInHours / 24);
    if (diffInDays < 7) return `${diffInDays}d ago`;
    return date.toLocaleDateString();
  };

  const getPriorityLevel = (index: number) => {
    if (index === 0) return 'high';
    if (index < 3) return 'medium';
    return 'normal';
  };

  const getActivityTitle = (item: any) => {
    if (item.type === 'email') {
      return item.subject || '(No Subject)';
    }
    
    const title = item.note || item.call_note || item.meeting_name || item.task || 'Activity';
    return title;
  };

  const getTaskCompletionIndicator = (item: any) => {
    return null;
  };

  const handleActivityClick = async (item: any) => {
    if (expandedEmailId === item.id) {
      return;
    }
    
    if (item.type === 'email') {
      // Open modal immediately with basic email data
      const basicActivityData = {
        id: item.id,
        oppositeContent: {
          date: item.created_on || item.email_creation_date,
          time: new Date(item.created_on || item.email_creation_date).toLocaleTimeString()
        },
        content: {
          title: `Email: ${item.subject || '(No Subject)'}`,
          user: 'System',
          details: `Email ${item.direction === 'sent' ? 'sent to' : 'received from'} ${item.direction === 'sent' ? item.to : item.from}`,
          email: {
            subject: item.subject || '(No Subject)',
            from: item.from || item.sender_email || 'Unknown',
            to: item.to || item.recipient_email || 'Unknown',
            body: item.body || item.email_body || 'Loading email content...',
            isCompressed: !!item.body_compressed,
            cc: item.cc || undefined,
            mail_id: item.mail_id
          }
        },
        separator: {
          icon: faEnvelope
        },
        attachments: [],
        isLoading: true
      };
      
      setSelectedActivity(basicActivityData);
      setShowModal(true);
      setIsLoadingEmailDetails(true);
      
      // Fetch detailed email data in background
      try {
        const endpoint = leadId
          ? `/leads/${leadId}/emails/${item.id}/detail`
          : `/emails/detail/${item.id}`;
        let response;

        try {
          response = await axiosInstance.get(endpoint);
        } catch (err: any) {
          if (leadId && err?.response?.status === 404) {
            response = await axiosInstance.get(`/emails/detail/${item.id}`);
          } else {
            throw err;
          }
        }

        const emailData = response.data.data;

        // Update with full email data including attachments
        const fullActivityData = {
          ...basicActivityData,
          content: {
            ...basicActivityData.content,
            email: {
              ...basicActivityData.content.email,
              body: emailData.body || item.body || item.email_body || 'No content available',
              isCompressed: !!emailData.body_compressed || !!item.body_compressed,
              mail_id: emailData.mail_id || item.mail_id
            }
          },
          attachments: emailData.attachments || [],
          isLoading: false
        };
        
        setSelectedActivity(fullActivityData);
      } catch (error) {
        console.error('Error fetching email details:', error);
        
        // Update with error state but keep modal open
        const errorActivityData = {
          ...basicActivityData,
          content: {
            ...basicActivityData.content,
            email: {
              ...basicActivityData.content.email,
              body: item.body || item.email_body || 'Error loading email content'
            }
          },
          attachments: [],
          isLoading: false
        };
        
        setSelectedActivity(errorActivityData);
      } finally {
        setIsLoadingEmailDetails(false);
      }
    }
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedActivity(null);
    setIsLoadingEmailDetails(false);
  };

  const isClickable = (item: any) => {
    return item.type === 'email' && (
      (item.body && item.body.trim() !== '') || 
      item.subject || 
      item.from || 
      item.to
    );
  };

  const handleQuickReply = (item: any, e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    setExpandedEmailId(expandedEmailId === item.id ? null : item.id);
    setReplyText('');
  };

  const handleSendReply = async (item: any) => {
    if (!replyText.trim()) return;
    setReplyText('');
    setExpandedEmailId(null);
  };

  const handleCancelReply = () => {
    setExpandedEmailId(null);
    setReplyText('');
  };

  const getEmailPreview = (
    body: string,
    isCompressed: boolean = false
  ): string => {
    let preview = body.replace(/<[^>]+>/g, '').trim();

    if (isCompressed) {
      preview = decodeEmailBody(preview, true);
    }

    preview = preview
      .replace(/📧 Forwarded Email[\s\S]*?$/, '')
      .replace(/Original Subject:[\s\S]*?$/, '')
      .replace(/Forwarded by:[\s\S]*?$/, '')
      .replace(/From:[\s\S]*?Sent:[\s\S]*?To:[\s\S]*?Subject:[\s\S]*?$/, '')
      .replace(/_{10,}/g, '')
      .replace(/Please consider the environment[\s\S]*?$/, '')
      .replace(/This electronic mail message[\s\S]*?$/, '')
      .replace(/Thanks,[\s\S]*$/, '')
      .replace(/Best regards,[\s\S]*$/, '')
      .replace(/Sincerely,[\s\S]*$/, '')
      .replace(/Best,[\s\S]*$/, '')
      .replace(/\s{3,}/g, ' ')
      .trim();

    return preview.length > 200
      ? `${preview.substring(0, 200)}...`
      : preview || 'No content available';
  };

  return (
    <div className="modern-activity-timeline mt-2">
      {/* Recent Activity Header */}
      <div className="activity-header mb-4">
        <div className="d-flex align-items-center justify-content-between">
          <div>
            <h6 className="mb-1 fw-semibold">Recent Customer Activity</h6>
            <small className="text-muted">Stay on top of your lead engagement</small>
          </div>
          <div className="d-flex align-items-center gap-2">
            <Badge bg="primary" className="fs-10">
              {isLoading ? '...' : `${allActivities.length} Activities`}
            </Badge>
          </div>
        </div>
      </div>

      {/* Show skeleton when loading */}
      {isLoading ? (
        <ActivityTimelineSkeleton />
      ) : (
        <>
          {/* Modern Activity Cards */}
          <div className="activity-feed">
            {allActivities.map((item: any, index: any) => {
          const ActivityIcon = getActivityIcon(item.type);
          const activityType = getActivityType(item.type);
          const priority = getPriorityLevel(index);
          const activityTitle = getActivityTitle(item);
          const clickable = isClickable(item);          
          
          return (
            <Card 
              key={item.id} 
              className={classNames(
                'activity-card mb-3 border-0',
                {
                  'recent-activity': index === 0,
                  'high-priority': priority === 'high',
                  'medium-priority': priority === 'medium',
                  'clickable-card': clickable
                }
              )}
              style={{ 
                cursor: clickable ? 'pointer' : 'default',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                ...(clickable && {
                  transform: 'scale(1)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
                })
              }}
              onMouseEnter={(e) => {
                if (clickable) {
                  e.currentTarget.style.transform = 'scale(1.02)';
                  e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.12)';
                }
              }}
              onMouseLeave={(e) => {
                if (clickable) {
                  e.currentTarget.style.transform = 'scale(1)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.08)';
                }
              }}
              onClick={() => clickable && handleActivityClick(item)}
            >
              <Card.Body className="p-3">
                <div className="d-flex align-items-start">
                  {/* Activity Icon */}
                  <div className={classNames(
                    'activity-icon me-3 flex-shrink-0',
                    `icon-${activityType.color}`
                  )}>
                    <ActivityIcon size="20" />
                  </div>

                  {/* Activity Content */}
                  <div className="activity-content flex-grow-1">
                    <div className="d-flex align-items-center justify-content-between mb-2">
                      <div className="d-flex align-items-center gap-2">
                        <Badge 
                          bg={activityType.color} 
                          className="activity-type-badge"
                        >
                          {activityType.type}
                        </Badge>
                        {index === 0 && (
                          <Badge bg="danger" className="fs-16">
                            Latest
                          </Badge>
                        )}
                        {priority === 'high' && (
                          <UilExclamationTriangle size="16" className="text-warning" />
                        )}

                      </div>
                      <small className="text-muted activity-time">
                        {getTimeAgo(item.created_on || item.email_creation_date)}
                      </small>
                    </div>

                    <h6 className={classNames(
                      'activity-title mb-2 fs-8',
                      {
                        'text-decoration-line-through text-muted': item.type === 'task' && (item.is_completed || item.status === 'Completed')
                      }
                    )}>
                      {activityTitle}
                      {getTaskCompletionIndicator(item)}
                    </h6>

                    {/* Email-specific content */}
                    {item.type === 'email' && (
                      <div className="email-activity-details mb-2">
                        <div className="d-flex align-items-center justify-content-between gap-2 mb-1">
                          <small className="text-muted">
                            <strong>{item.direction === 'sent' ? 'To:' : 'From:'}</strong> {item.direction === 'sent' ? item.to : item.from}
                          </small>
                          {/* Quick Actions */}
                          <div className="email-quick-actions d-flex gap-1">
                            <Button
                              variant="outline-primary"
                              size="sm"
                              className="btn-sm px-2 py-1"
                              onClick={(e) => handleQuickReply(item, e)}
                              title="Quick Reply"
                            >
                              <FontAwesomeIcon icon={faReply} size="xs" />
                            </Button>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              className="btn-sm px-2 py-1"
                              onClick={(e) => {
                                e.stopPropagation();
                                e.preventDefault();
                                console.log('View Full Email button clicked for item:', item);
                                // Only open the modal if we're not in quick reply mode
                                if (expandedEmailId !== item.id) {
                                  handleActivityClick(item);
                                }
                              }}
                              title="View Full Email"
                            >
                              <FontAwesomeIcon icon={faEnvelope} size="xs" />
                            </Button>
                          </div>
                        </div>
                        {item.body && (
                          <p
                            className="email-preview mb-0 text-muted"
                            style={{
                              fontSize: '0.8rem',
                              lineHeight: '1.3',
                              maxHeight: '2.6rem',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical' as const,
                            }}
                          >
                            {getEmailPreview(
                              item.body_compressed ? item.body_compressed : item.body,
                              !!item.body_compressed
                            )}
                          </p>
                        )}
                        {/* Quick Reply Form */}
                        <Collapse in={expandedEmailId === item.id}>
                          <div 
                            className="quick-reply-form mt-3 p-3 bg-light rounded" 
                            onClick={(e) => e.stopPropagation()} 
                            onFocus={(e) => e.stopPropagation()}
                          >
                            <div className="d-flex align-items-center justify-content-between mb-2">
                              <small className="fw-semibold text-primary">
                                <FontAwesomeIcon icon={faReply} className="me-1" />
                                Quick Reply to {item.from || item.sender_email}
                              </small>
                              <Button
                                variant="link"
                                size="sm"
                                className="p-0 text-muted"
                                onClick={handleCancelReply}
                              >
                                <FontAwesomeIcon icon={faTimes} />
                              </Button>
                            </div>
                            <Form.Control
                              as="textarea"
                              rows={3}
                              placeholder="Type your reply here..."
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              onClick={(e) => e.stopPropagation()} // Prevent modal opening on click
                              onFocus={(e) => e.stopPropagation()} // Prevent modal opening on focus
                              className="mb-2"
                            />
                            <div className="d-flex justify-content-end gap-2">
                              <Button
                                variant="outline-secondary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleCancelReply();
                                }}
                              >
                                Cancel
                              </Button>
                              <Button
                                variant="primary"
                                size="sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  e.preventDefault();
                                  handleSendReply(item);
                                }}
                                disabled={!replyText.trim()}
                              >
                                <FontAwesomeIcon icon={faPaperPlane} className="me-1" />
                                Send Reply
                              </Button>
                            </div>
                          </div>
                        </Collapse>
                      </div>
                    )}

                    <div className="activity-footer d-flex align-items-center justify-content-between">
                      <div className="activity-user d-flex align-items-center">
                        <div className="user-avatar me-2">
                          <div className="avatar-circle bg-secondary" style={{ 
                            borderRadius: '50%',
                            width: '26px',
                            height: '26px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            overflow: 'hidden'
                          }}>
                            <UilUser size="12" className="text-white" />
                          </div>
                        </div>
                        <small className="text-muted">
                          <FontAwesomeIcon
                            icon={faCalendarDays}
                            className="text-primary me-1"
                            size="sm"
                          />
                          {dateFormatter(item.created_on || item.email_creation_date)}
                        </small>
                      </div>
                    </div>
                  </div>
                </div>
              </Card.Body>
            </Card>
          );
            })}
          </div>
          
          {/* Modern Pagination */}
          {allActivities.length > 0 && (
            <div className="modern-pagination mt-4 d-flex justify-content-center">
              <Pagination className="modern-pagination-controls">
                <Pagination.Prev
                  disabled={offset + 1 === 1}
                  onClick={handlePrevious}
                  className="pagination-control"
                >
                  <FontAwesomeIcon icon={faChevronLeft} />
                </Pagination.Prev>
                {getPageNumbers()}
                <Pagination.Next
                  disabled={offset + 1 === totalPages}
                  onClick={handleNext}
                  className="pagination-control"
                >
                  <FontAwesomeIcon icon={faChevronRight} />
                </Pagination.Next>
              </Pagination>
            </div>
          )}
        </>)}

      {/* Email Detail Modal */}
      <EmailDetailModal 
        show={showModal}
        onHide={handleCloseModal}
        activityData={selectedActivity}
        isLoadingDetails={isLoadingEmailDetails}
      />
    </div>
  );
};

export default LeadDetailsTimeline;
